/**
 * ReviewPromptCard Component
 *
 * Slim, dismissible Start Page card that asks established users for a
 * review (via the jamsql.com/review/ chooser page) once the state machine has "armed"
 * (see `reviewPromptStore`). Renders nothing otherwise. Persists across
 * sessions until the user clicks through or dismisses it.
 */

import { useEffect } from 'react';
import { Star } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { useReviewPromptStore } from '../store/reviewPromptStore';
import { reviewUrl } from '../reviewPromptConstants';

// Module-level guard so `feature:review_prompt_shown` fires at most once per
// app session, even if the card unmounts/remounts (e.g. switching tabs).
let hasTrackedShownThisSession = false;

export function ReviewPromptCard() {
  const { track } = useAnalytics();
  const promptState = useReviewPromptStore((state) => state.promptState);
  const markClicked = useReviewPromptStore((state) => state.markClicked);
  const markDismissed = useReviewPromptStore((state) => state.markDismissed);
  const recordCardShownSession = useReviewPromptStore((state) => state.recordCardShownSession);

  const isArmed = promptState === 'armed';

  useEffect(() => {
    if (!isArmed || hasTrackedShownThisSession) return;
    hasTrackedShownThisSession = true;
    // Feeds auto-retirement: after CARD_MAX_SHOWN_SESSIONS ignored sessions,
    // the next boot retires the card (see reviewPromptStore).
    recordCardShownSession();
    track('feature:review_prompt_shown', { surface: 'card' });
  }, [isArmed, track, recordCardShownSession]);

  if (!isArmed) return null;

  const handleWriteReview = () => {
    track('feature:review_prompt_click', { surface: 'card' });
    markClicked();
  };

  const handleDismiss = () => {
    track('feature:review_prompt_dismiss');
    markDismissed();
  };

  return (
    <div
      className="flex flex-col items-start gap-3 rounded-lg border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid="review-prompt-card"
    >
      <div className="flex items-center gap-3">
        <Star className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400 fill-amber-500" aria-hidden="true" />
        <p className="text-sm">
          <span className="font-medium">Enjoying Jam SQL Studio?</span>{' '}
          <span className="text-muted-foreground">
            A short review helps other developers find it.
          </span>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          data-testid="review-prompt-card-dismiss"
        >
          No thanks
        </Button>
        <Button asChild size="sm" onClick={handleWriteReview} data-testid="review-prompt-card-cta">
          <a href={reviewUrl('start-page-card')} target="_blank" rel="noopener noreferrer">
            Write a review
          </a>
        </Button>
      </div>
    </div>
  );
}
